import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MangaCard from '@/components/feature/MangaCard';
import { mangaList } from '@/mocks/mangaData';

export default function MangaDetail() {
  const { id } = useParams<{ id: string }>();
  const manga = mangaList.find((m) => m.id === id);

  if (!manga) {
    return (
      <>
        <Navbar />
        <div className="pt-24 text-center min-h-[400px] flex flex-col items-center justify-center px-4">
          <div className="w-16 h-16 flex items-center justify-center mb-4">
            <i className="ri-file-unknow-line text-foreground-300 text-3xl"></i>
          </div>
          <h2 className="font-heading text-xl text-foreground-700 mb-2">漫画未找到</h2>
          <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium cursor-pointer whitespace-nowrap">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const relatedManga = mangaList
    .filter((m) => m.id !== manga.id && m.category === manga.category)
    .slice(0, 6);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${manga.title} - ${manga.categoryLabel}漫画`,
    description: manga.description,
    author: { '@type': 'Person', name: manga.author },
    datePublished: manga.chapters[0]?.updatedAt,
    dateModified: manga.updatedAt,
  };

  return (
    <>
      <SEOHead
        title={`${manga.title} - ${manga.categoryLabel}漫画免费在线阅读 | 女神漫画`}
        description={`${manga.title}：${manga.description.substring(0, 120)}。女神漫画提供${manga.categoryLabel}漫画免费全集下拉式阅读，支持追更收藏。`}
        keywords={`${manga.title},${manga.categoryLabel},${manga.tags.join(',')},女神漫画,在线漫画,免费全集`}
        canonicalUrl={`https://goddessmanga.com/manga/${manga.id}`}
        ogType="article"
        schema={schema}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        {/* Manga Detail Header */}
        <section className="py-8 md:py-12 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: manga.categoryLabel, path: `/category/${manga.category}` },
                { label: manga.title },
              ]}
            />

            <div className="flex flex-col md:flex-row gap-6 md:gap-10 mt-6">
              {/* Cover */}
              <div className="flex-shrink-0">
                <div className="w-48 md:w-56 aspect-[3/4] rounded-lg overflow-hidden">
                  <img
                    src={manga.coverUrl}
                    alt={`${manga.title} - ${manga.categoryLabel}漫画`}
                    title={`${manga.title} - ${manga.categoryLabel}漫画推荐`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-2">
                  {manga.title}
                </h1>
                <p className="text-sm text-foreground-600 mb-3">作者：{manga.author}</p>

                <div className="flex items-center gap-3 flex-wrap mb-4">
                  <span className="px-2.5 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">{manga.categoryLabel}</span>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${manga.status === '已完结' ? 'bg-secondary-100 text-secondary-700' : 'bg-accent-100 text-accent-700'}`}>
                    {manga.status}
                  </span>
                  {manga.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/category/${manga.category}`}
                      className="px-2 py-0.5 bg-background-100 text-foreground-600 text-xs rounded cursor-pointer whitespace-nowrap hover:bg-background-200 transition-colors"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>

                <div className="flex items-center gap-5 text-sm text-foreground-600 mb-4 flex-wrap">
                  <span className="flex items-center gap-1">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500"></i>
                    </div>
                    <strong className="text-foreground-800">{manga.rating}</strong> 分
                  </span>
                  <span>{manga.ratingCount.toLocaleString()} 人评分</span>
                  <span>{manga.views.toLocaleString()} 次阅读</span>
                  <span>{manga.collects.toLocaleString()} 人收藏</span>
                </div>

                <p className="text-sm text-foreground-700 leading-relaxed mb-6">{manga.description}</p>

                <div className="flex items-center gap-3 flex-wrap">
                  <button className="px-6 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-primary-600 transition-colors">
                    开始阅读
                  </button>
                  <button className="px-6 py-2.5 bg-secondary-500 text-background-50 dark:text-foreground-950 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-secondary-600 transition-colors">
                    加入收藏
                  </button>
                  <span className="text-xs text-foreground-500">最后更新：{manga.updatedAt}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Chapter List */}
        <section className="py-8 md:py-12 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 mb-6">
              章节列表 <span className="text-sm font-normal text-foreground-500">（共 {manga.chapters.length} 话）</span>
            </h2>

            <div className="space-y-2">
              {manga.chapters.sort((a, b) => b.index - a.index).map((chapter) => (
                <div
                  key={chapter.id}
                  className="flex items-center justify-between px-4 py-3 bg-background-50 rounded-lg cursor-pointer hover:bg-primary-50 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xs text-foreground-500 font-mono whitespace-nowrap"># {chapter.index}</span>
                    <span className="text-sm text-foreground-800 truncate">{chapter.title}</span>
                  </div>
                  <span className="text-xs text-foreground-500 whitespace-nowrap ml-4">{chapter.updatedAt}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Manga */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 mb-6">相关推荐</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
              {relatedManga.map((rm) => (
                <MangaCard key={rm.id} manga={rm} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}